import { useCallback, useEffect, useMemo, useState } from "react";
import { Mail, X } from "lucide-react";
import { useMailProviders } from "@/hooks/useMailProviders";
import { MailModalOverlay } from "@/features/shared/MailModalOverlay";
import { MailLoadingState } from "@/features/shared/MailLoadingState";
import { connectGmailWithGoogle } from "@/lib/accounts/gmailOAuthPopup";
import { connectMicrosoftMailbox } from "@/lib/accounts/microsoftOAuthPopup";
import type { MailAccountSummary } from "@/lib/types/mail";
import { AccountConnectPicker } from "./AccountConnectPicker";
import { AccountConnectForm } from "./AccountConnectForm";
import { mailConnectIsOAuthOnlyProvider } from "./providerConnectOrder";

type Props = {
  open: boolean;
  onClose: () => void;
  onConnected: (account?: MailAccountSummary) => void;
};

export function AccountConnectModal({ open, onClose, onConnected }: Props) {
  const { providers, loading } = useMailProviders();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [microsoftBusy, setMicrosoftBusy] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);

  const selected = useMemo(
    () => providers.find((p) => p.id === selectedId) ?? null,
    [providers, selectedId]
  );

  useEffect(() => {
    if (!open) {
      setSelectedId(null);
      setGoogleBusy(false);
      setMicrosoftBusy(false);
      setOauthError(null);
    }
  }, [open]);

  const handleGoogleConnect = useCallback(async () => {
    if (googleBusy || microsoftBusy) return;

    setGoogleBusy(true);
    setOauthError(null);
    try {
      const account = await connectGmailWithGoogle();
      onConnected(account);
      onClose();
    } catch (err) {
      setOauthError(err instanceof Error ? err.message : "Connexion Google impossible.");
    } finally {
      setGoogleBusy(false);
    }
  }, [googleBusy, microsoftBusy, onClose, onConnected]);

  const handleMicrosoftConnect = useCallback(async () => {
    if (googleBusy || microsoftBusy) return;

    setMicrosoftBusy(true);
    setOauthError(null);
    try {
      const account = await connectMicrosoftMailbox();
      onConnected(account);
      onClose();
    } catch (err) {
      setOauthError(err instanceof Error ? err.message : "Connexion Microsoft impossible.");
    } finally {
      setMicrosoftBusy(false);
    }
  }, [googleBusy, microsoftBusy, onClose, onConnected]);

  return (
    <MailModalOverlay
      open={open}
      onClose={onClose}
      labelledBy="account-connect-modal-title"
      panelClassName="max-w-3xl max-h-[min(90vh,720px)] overflow-y-auto"
    >
      <div className="flex items-start justify-between gap-3 border-b border-[var(--mail-border-soft)] px-5 py-4">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--mail-accent-soft)]">
            <Mail className="h-5 w-5 text-[var(--mail-accent)]" aria-hidden />
          </span>
          <div className="min-w-0">
            <h2
              id="account-connect-modal-title"
              className="text-base font-semibold tracking-tight text-[var(--mail-text)]"
            >
              Ajouter une boîte mail
            </h2>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 rounded-lg p-1.5 text-[var(--mail-muted)] transition hover:bg-[var(--mail-surface-hover)] hover:text-[var(--mail-text)]"
          aria-label="Fermer"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>

      <div className="space-y-6 px-5 py-5">
        <section>
          {loading ? (
            <MailLoadingState variant="webmail" layout="compact" className="py-6" />
          ) : (
            <AccountConnectPicker
              providers={providers}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onGoogleConnect={() => void handleGoogleConnect()}
              onMicrosoftConnect={() => void handleMicrosoftConnect()}
              googleBusy={googleBusy}
              microsoftBusy={microsoftBusy}
            />
          )}
          {oauthError ? (
            <p className="mt-3 text-sm text-[var(--mail-danger-text)]" role="alert">
              {oauthError}
            </p>
          ) : null}
        </section>

        {selected && !mailConnectIsOAuthOnlyProvider(selected, providers) ? (
          <section>
            <h3 className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--mail-muted)]">
              Identifiants — {selected.label}
            </h3>
            <AccountConnectForm
              provider={selected}
              onSuccess={(account) => onConnected(account)}
              hideGoogleOAuth
              hideMicrosoftOAuth
            />
          </section>
        ) : null}
      </div>
    </MailModalOverlay>
  );
}
